/**
 * PassagePanel Component
 * Displays the reading passage in a fixed left panel for split-panel layout (desktop/landscape only)
 * Hidden on narrow viewports (< 1024px) where PassageCard is used instead
 *
 * Redesign (2026-06): the passage is "encased" as a piece of paper — a slightly tilted
 * cream sheet with washi tape and faint stacked pages behind it, floating on a warm sand
 * stage, set in the Lora serif (font-reading) for a storybook feel.
 *
 * IMPORTANT — line-numbered passages: PSLE passages embed line-number prefixes (1., 5.,
 * 10., …) at the start of FIXED physical lines, and students reference "line 7". The text
 * is therefore rendered with `whitespace-pre` (hard breaks preserved, NO wrapping) so the
 * numbered lines never reflow; long lines scroll horizontally (overflow-x-auto) instead of
 * wrapping. The panel is a 38/62 split so the lines have room to fit without scrolling.
 *
 * Highlighter (2026-07): opt-in via `highlightSessionId`. Without it this renders exactly as
 * before — a single plain <p> — which is what keeps the parent's read-only session view free
 * of the pen, and keeps text queries matching whole phrases rather than per-word spans.
 */

import { usePassageHighlights } from '@/hooks/usePassageHighlights';
import { usePassageAnnotations } from '@/hooks/usePassageAnnotations';
import { PassageToolbars } from '@/components/PassageToolbars';
import { HighlightablePassageText } from '@/components/HighlightablePassageText';
import { usePassageCopyGuard } from '@/lib/passageCopyGuard';

export interface PassagePanelProps {
  /** Reading passage text */
  passage: string;
  /**
   * Enables the highlighter, scoped to this session. Omit for a read-only passage.
   * Highlights persist in localStorage per session; they are not shared across devices.
   */
  highlightSessionId?: string;
}

/**
 * PassagePanel - Full-height passage panel for split-panel layout
 *
 * Features:
 * - Hidden below 1024px (narrow mode uses inline PassageCard)
 * - Occupies 38% width on desktop/landscape viewports (>= 1024px)
 * - Independent vertical scrolling for long passages
 * - "Piece of paper" treatment on a warm sand stage
 * - Line-numbered text preserved verbatim (no wrap) so line references stay accurate
 *
 * @example
 * ```tsx
 * <PassagePanel passage={passage ?? ''} />
 * ```
 */
export function PassagePanel({ passage, highlightSessionId }: PassagePanelProps) {
  // Called before the early return: hooks must run unconditionally. Inert without a sessionId.
  const highlights = usePassageHighlights(highlightSessionId, passage);
  const annotations = usePassageAnnotations(highlightSessionId, passage);
  const { ref: copyGuardRef, guardProps } = usePassageCopyGuard<HTMLDivElement>();

  if (!passage || passage.trim() === '') {
    return null;
  }

  const passageClassName =
    'overflow-x-auto whitespace-pre font-reading text-base leading-[1.7] text-otter-earth-800';

  return (
    <div
      className="hidden landscape:flex landscape:w-[38%] lg:flex lg:w-[38%] items-start justify-center overflow-y-auto border-r border-[#E7D8C8] p-5 lg:p-6 transition-all duration-300 ease-in-out"
      style={{
        background:
          'radial-gradient(80% 55% at 30% 8%, rgba(251,191,36,0.10), transparent 60%), linear-gradient(160deg, #fbedd4, #f3e0c2)',
      }}
    >
      {/* Paper sheet — straight/vertical, with faint stacked pages behind it.
          Stacked sheets are offset DOWNWARD only (no rotation / no horizontal shift) so the
          clipboard stays within the panel width and never triggers a horizontal scrollbar. */}
      <div className="relative w-full">
        {/* Stacked back-sheets (decorative, straight) */}
        <div
          aria-hidden="true"
          className="absolute inset-0 z-0 translate-y-[6px] rounded-[10px] border border-[#f0e6d2] bg-[#fbf2df] shadow-md"
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 z-0 translate-y-[3px] rounded-[10px] border border-[#f0e6d2] bg-[#fdf7ea] shadow-md"
        />
        {/* Washi tape — straight, centered */}
        <span
          aria-hidden="true"
          className="absolute z-[2] left-1/2 -top-3 h-[26px] w-[118px] -translate-x-1/2 rounded-[2px] border border-[rgba(217,119,6,0.25)]"
          style={{
            background:
              'linear-gradient(135deg, rgba(253,224,154,0.75), rgba(251,191,36,0.55))',
            boxShadow: '0 2px 6px rgba(120,53,15,0.15)',
          }}
        />
        {/* Front sheet. The copy guard registers this element so it covers both the plain and
            highlightable passage branches; selection itself stays untouched. */}
        <div
          ref={copyGuardRef}
          className="relative z-[1] rounded-[10px] border border-[#efe4cf] bg-[#fffdf7] px-5 py-6 shadow-lg lg:px-7"
          {...guardProps}
        >
          <div className="mb-2.5 flex items-center gap-1.5">
            <span aria-hidden="true" className="text-base">
              📖
            </span>
            <h2 className="font-display text-xl font-semibold text-otter-earth-800">
              Reading Passage
            </h2>
          </div>
          {highlights.enabled && (
            <PassageToolbars
              armedShape={annotations.armedShape}
              onArmShape={annotations.setArmedShape}
              onUndoMark={annotations.undo}
              onClearMarks={annotations.clearAll}
              hasMarks={annotations.marks.length > 0}
              armedColor={highlights.armedColor}
              onArmColor={highlights.setArmedColor}
              onClearHighlights={highlights.clearAll}
              hasHighlights={highlights.ranges.length > 0}
            />
          )}
          {/* Line-numbered passage: preserve exact lines (no wrap); scroll long lines. */}
          {highlights.enabled ? (
            <HighlightablePassageText
              tokens={highlights.tokens}
              ranges={highlights.ranges}
              armedColor={highlights.armedColor}
              onHighlightRange={highlights.highlightRange}
              onEraseAt={highlights.eraseAt}
              className={passageClassName}
              annotations={{
                armedShape: annotations.armedShape,
                marks: annotations.marks,
                pendingLink: annotations.pendingLink,
                onMarkRange: annotations.markRange,
                onEraseMarkAt: annotations.eraseAt,
              }}
            />
          ) : (
            <p className={passageClassName}>{passage}</p>
          )}
        </div>
      </div>
    </div>
  );
}
